// @ts-nocheck
'use client'

import InstantSell from '@/components/offers/instant-sell'
import MarketTile from '@/components/offers/market-tile'
import React, { FC, ReactElement, useMemo, useEffect, useState } from 'react'
import { TEXT_COLLECTION_SLUGS } from '@/utils/constants'

import useCollectionInscriptions from '@/utils/hooks/useCollectionInscriptions'
import Loading from '@/components/circle-loading'
import InfiniteScroll from 'react-infinite-scroll-component'
import InscriptionCardWithPreview from '@/components/inscription-card-with-preview'
import InscriptionActions from '@/components/inscription-actions'
import EmptyState from '@/components/empty-state'
import { Collection } from '@/utils/types/collections'
import { Inscription } from '@/utils/types/inscription'
import useDebounce from '@/utils/hooks/useDebounce'
import { Attribute } from '@/utils/types/collections'
import parseDate from '@/utils/parse-date'
import isFungible from '@/utils/is-fungible'
import useComponentSize from '@/utils/hooks/useComponentSize'
import isRune from '@/utils/is-rune'
import RuneCard from '@/components/rune-card'
import useRuneStats from '@/utils/hooks/useRuneStats'
import useEscrows from '@/utils/hooks/useEscrows'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Filter, BarChart2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import TraitFilters from '@/components/trait-filters'
import event from '@/utils/event'
import styles from './CollectionFeed.module.css'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import TvCollectionChart from '../tv-collection-chart'
import useCollectionRanks from '@/utils/hooks/useCollectionRanks'
import { useMonkeFilters, useMonkeCatalog, useSavedMonkes } from '@/utils/hooks/useMonkeDiscovery'
import { isDiscoveryFilter, matchesDiscoveryFilters } from '@/utils/nodemonkes'
import MonkeDiscoveryControls from '@/components/nodemonkes/discovery-controls'


const RARITY_FILTER_TRAIT = '__rarity_tier'
const RARITY_TIER_OPTIONS = ['Exotic', 'Legendary', 'Epic', 'Rare', 'Uncommon', 'Common']

let _INITIAL_RENDER = false
const LIMIT = 100

const CollectionFeed: FC<{
	title: string
	items: any[]
	slug: string
	supply: number
	listed: number
	meta: Collection
	isMobile: boolean
	attributes?: Attribute[]
	onSelectBulk: (id: Inscription) => void
	bulkSelected: any
	disablePreview?: boolean
}> = (props): ReactElement => {
	const { onSelectBulk, disablePreview } = props

	// Track selection state locally, synced with add-to-cart events
	const [localSelected, setLocalSelected] = useState<Record<string, boolean>>({})

	// Listen for add-to-cart events to sync selection state
	useEffect(() => {
		const handleAddToCart = (payload: { id: string }) => {
			setLocalSelected((prev) => {
				const newState = { ...prev }
				if (newState[payload.id]) {
					delete newState[payload.id]
				} else {
					newState[payload.id] = true
				}
				return newState
			})
		}

		event.listen('add-to-cart', handleAddToCart)
		return () => {
			event.removeListener('add-to-cart', handleAddToCart)
		}
	}, [])

	// Merge with bulkSelected prop
	const bulkSelected = useMemo(() => {
		return { ...props.bulkSelected, ...localSelected }
	}, [props.bulkSelected, localSelected])
	const { ref } = useComponentSize()

	const isNames = TEXT_COLLECTION_SLUGS.includes(props.slug)
	const [selected, setSelected] = React.useState('PriceAsc')
	const [_gridView, _setGridView] = React.useState(!props.isMobile && !isNames)
	const [chartExpanded, setChartExpanded] = React.useState(false)
	const [selectedTab, setSelectedTab] = React.useState(isRune(props.slug) ? 'listed' : 'show-all')
	const [filters, setFilters] = useMonkeFilters(props.slug)
	const { byId: monkeCatalog } = useMonkeCatalog(props.slug === 'nodemonkes')
	const { saved: savedMonkes } = useSavedMonkes()
	const [search, _setSearch] = React.useState('')
	const debouncedValue: string = useDebounce<string>(search, 300)
	const [size, setSize] = React.useState(0)
	const { data, isLoading } = useCollectionInscriptions(props.slug)
	const { data: escrows } = useEscrows(props.slug)
	const { data: ranksData } = useCollectionRanks(props.slug)
	const ranksMap = ranksData?.ranks
	const { data: rawRuneData } = useRuneStats(props.slug)
	const runeData = useMemo(() => (isRune(props.slug) ? rawRuneData : {}), [props.slug, rawRuneData])

	const escrowsMap = React.useMemo(() => {
		const map = {}
		escrows?.forEach((e) => {
			map[e.inscription_id] = e
		})

		return map
	}, [escrows])

	const inscriptions = useMemo(() => {
		if (!isRune(props.slug) && (!data || data.length === 0)) {
			return []
		}

		if (isRune(props.slug) && (!escrows || escrows.length === 0)) {
			return []
		}

		let res = isRune(props.slug)
			? (escrows || []).map((e) => ({
					...runeData,
					...e,
					collection: { slug: props.slug },
					escrow: e,
					id: e.outpoint,
					ticker: runeData?.name
			  }))
			: (data || []).map((e) => ({ ...e, escrow: escrowsMap[e.id] }))

		if (selectedTab === 'listed') {
			res = res.filter((e) => e?.escrow && e.escrow.satoshi_price)
		}

		// Define the sorting function
		const sortFunction = (a, b) => {
			switch (selected) {
				case 'PriceAsc':
					if (isRune(props.slug) || isFungible(props.slug)) {
						const aRate =
							(a?.escrow?.satoshi_price || Number.MAX_VALUE) /
							parseInt(
								a?.escrow.amount ||
									a?.meta?.attributes?.find((e) => e.trait_type === 'amount')?.value ||
									'1'
							)
						const bRate =
							(b?.escrow?.satoshi_price || Number.MAX_VALUE) /
							parseInt(
								b?.escrow.amount ||
									b?.meta?.attributes?.find((e) => e.trait_type === 'amount')?.value ||
									'1'
							)
						return aRate - bRate
					}
					return (
						(a?.escrow?.satoshi_price || Number.MAX_VALUE) -
						(b?.escrow?.satoshi_price || Number.MAX_VALUE)
					)
				case 'PriceDesc':
					if (isRune(props.slug) || isFungible(props.slug)) {
						const aRate =
							(a?.escrow?.satoshi_price || 0) /
							parseInt(
								a?.escrow.amount ||
									a?.meta?.attributes?.find((e) => e.trait_type === 'amount')?.value ||
									'1'
							)
						const bRate =
							(b?.escrow?.satoshi_price || 0) /
							parseInt(
								b?.escrow.amount ||
									b?.meta?.attributes?.find((e) => e.trait_type === 'amount')?.value ||
									'1'
							)
						return bRate - aRate
					}
					return (b?.escrow?.satoshi_price || 0) - (a?.escrow?.satoshi_price || 0)
				case 'RankAsc': {
					const aRank =
						ranksMap?.[a?.id]?.rank ?? a?.meta?.rank ?? Number.MAX_VALUE
					const bRank =
						ranksMap?.[b?.id]?.rank ?? b?.meta?.rank ?? Number.MAX_VALUE
					return aRank - bRank
				}
				case 'RankDesc': {
					const aRank = ranksMap?.[a?.id]?.rank ?? a?.meta?.rank ?? 0
					const bRank = ranksMap?.[b?.id]?.rank ?? b?.meta?.rank ?? 0
					return bRank - aRank
				}
				case 'InscriptionAsc':
					return (a?.num || Number.MAX_VALUE) - (b?.num || Number.MAX_VALUE)
				case 'InscriptionDesc':
					return (b?.num || 0) - (a?.num || 0)
				case 'ListedDesc':
					const aTime = a?.escrow?.created ? parseDate(a.escrow.created).getTime() : 0
					const bTime = b?.escrow?.created ? parseDate(b.escrow.created).getTime() : 0
					return bTime - aTime
				default:
					return 0
			}
		}

		res.sort(sortFunction)

		if (debouncedValue) {
			res = res.filter((i) => i?.meta?.name?.toLowerCase().includes(debouncedValue.toLowerCase()))
		}

		if (props.slug === 'nodemonkes') res = res.filter((i) => matchesDiscoveryFilters(i.id, filters, savedMonkes, monkeCatalog))

		if (filters?.length > 0) {
			res = res.filter((i) => {
				const satributes = i?.satributes?.map((s) => ({ trait_type: 'Satributes', value: s }))
				const attributes = [...(i?.meta?.attributes || []), ...(satributes || [])]

				return filters.every((filter) => {
					if (isDiscoveryFilter(filter.trait_type)) return true
					// Pseudo-trait: rarity tier (computed client-side from collection ranks)
					if (filter?.trait_type === RARITY_FILTER_TRAIT) {
						const tierLabel = ranksMap?.[i?.id]?.tier?.label
						return tierLabel && filter.values.includes(tierLabel)
					}
					const attr = attributes.find((a) => a?.trait_type === filter?.trait_type)
					return filter.values.includes(attr?.value)
				})
			})
		}

		return res.slice(0, (1 + size) * LIMIT)
	}, [
		data,
		escrowsMap,
		selected,
		selectedTab,
		filters,
		size,
		debouncedValue,
		escrows,
		runeData,
		props.slug,
		ranksMap, savedMonkes, monkeCatalog
	])

	const hasRank = Boolean(ranksMap || inscriptions?.[0]?.meta?.rank)

	const options = React.useMemo(() => {
		return [
			{
				title: 'Lowest price',
				id: 'PriceAsc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('PriceAsc')
				}
			},
			hasRank && {
				title: 'Rarest first',
				id: 'RankAsc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('RankAsc')
				}
			},
			hasRank && {
				title: 'Most common first',
				id: 'RankDesc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('RankDesc')
				}
			},
			{
				title: 'Highest price',
				id: 'PriceDesc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('PriceDesc')
				}
			},
			!isRune(props.slug) && {
				title: 'Lowest inscription',
				id: 'InscriptionAsc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('InscriptionAsc')
				}
			},
			!isRune(props.slug) && {
				title: 'Highest inscription',
				id: 'InscriptionDesc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('InscriptionDesc')
				}
			},
			{
				title: 'Recently listed',
				id: 'ListedDesc',
				onClick: () => {
					_INITIAL_RENDER = false
					setSelected('ListedDesc')
				}
			}
		].filter((e) => !!e)
	}, [hasRank, props.slug])

	const TABS = React.useMemo(
		() =>
			[
				{
					title: 'For Sale',
					selected: selectedTab === 'listed',
					onClick: () => setSelectedTab('listed')
				},
				!isRune(props.slug) && {
					title: 'Show All',
					selected: selectedTab === 'show-all',
					onClick: () => setSelectedTab('show-all')
				},
				// An NFT collection's chart sits above its items (see below);
				// fungibles and runes keep it behind a tab.
				(isRune(props.slug) || isFungible(props.slug)) && {
					title: 'Chart',
					selected: selectedTab === 'chart',
					onClick: () => setSelectedTab('chart')
				}
			].filter((e) => !!e),
		[selectedTab, props.slug]
	)

	const chartInline = !isRune(props.slug) && !isFungible(props.slug)

	const handleShowAll = React.useCallback(() => setSelectedTab('show-all'), [])

	const updateFilters = React.useCallback((trait_type, values) => {
		_INITIAL_RENDER = false

		const current = { trait_type, values }
		setFilters((prev) => {
			const index = prev.findIndex((e) => e.trait_type === trait_type)

			if (values?.length === 0) {
				if (index !== -1) {
					prev.splice(index, 1)
					return [...prev]
				}
				return prev
			} else {
				if (index !== -1) {
					prev[index] = current
					return [...prev]
				} else {
					return [...prev, current]
				}
			}
		})
	}, [setFilters])

	useEffect(() => setSize(0), [filters])

	const fetchMore = React.useCallback(async () => setSize((prev) => prev + 1), [])

	const isBitmap = props.slug === 'bitmap'
	const collectionAttributes =
		!isNames || isBitmap
			? props?.attributes
			: props?.attributes?.length > 0
			? [
					{
						trait_type: 'Characters',
						values: props.attributes[0].values.sort((a, b) => parseInt(a.value) - parseInt(b.value))
					}
			  ]
			: []

	const attributes =
		props?.satributes?.length > 0
			? [...collectionAttributes, ...props?.satributes]
			: collectionAttributes

	const hasAttributes = attributes?.length > 0 && !isFungible(props.slug)
	const hasRarityFilter = !!ranksMap && !isFungible(props.slug)
	const showFilterSheet = hasAttributes || hasRarityFilter

	const [isFilterOpen, setIsFilterOpen] = React.useState(false)

	const activeRarityValues = React.useMemo(() => {
		return filters.find((f) => f?.trait_type === RARITY_FILTER_TRAIT)?.values || []
	}, [filters])
	const toggleRarityValue = React.useCallback(
		(label) => {
			const current = filters.find((f) => f?.trait_type === RARITY_FILTER_TRAIT)?.values || []
			const next = current.includes(label)
				? current.filter((v) => v !== label)
				: [...current, label]
			updateFilters(RARITY_FILTER_TRAIT, next)
		},
		[filters, updateFilters]
	)

	return (
		<section className={`${styles.container} w-full overflow-hidden`} ref={ref}>
			{/* Fixed top section */}
			<div className="sticky top-0 bg-background z-10 border-b border-input">
				<div className="md:py-4 py-3 px-4 flex flex-wrap justify-between items-start sm:items-center gap-2 sm:gap-4">
					{/* Implement Shadcn Tabs using TABS object */}
					<Tabs defaultValue={selectedTab} onValueChange={(value) => setSelectedTab(value)}>
						<TabsList
							className="grid w-full h-7 sm:h-10"
							style={{ gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))` }}
						>
							{TABS.map((tab) => (
								<TabsTrigger
									key={tab.title}
									value={tab.title.toLowerCase().replace(' ', '-')}
									onClick={tab.onClick}
									className="text-xs sm:text-sm px-2 py-0.5 sm:px-3 sm:py-1.5"
								>
									{tab.title}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>
					<div className="flex items-center gap-x-1.5 sm:space-x-2">
						{/* Implement Shadcn Select using options object */}
						<Select
							defaultValue={selected}
							onValueChange={(value) => {
								const option = options.find((opt) => opt.id === value)
								if (option) {
									option.onClick()
								}
							}}
						>
							<SelectTrigger className="w-[130px] sm:w-[180px] h-7 sm:h-10 text-xs sm:text-sm">
								<SelectValue placeholder="Sort by" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectLabel>Sort Options</SelectLabel>
									{options.map((option) => (
										<SelectItem key={option.id} value={option.id}>
											{option.title}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>

                        {chartInline && <Button
                            variant="outline"
                            size="icon"
                            className="size-7 sm:h-10 sm:w-10"
                            aria-label={chartExpanded ? 'Collapse chart' : 'Expand chart'}
                            aria-expanded={chartExpanded}
                            aria-controls="collection-inline-chart"
                            onClick={() => setChartExpanded((expanded) => !expanded)}
                        ><BarChart2 className="size-3 sm:h-4 sm:w-4" /></Button>}
						{/* Only show the Sheet if there are attributes or rarity ranks */}
						{showFilterSheet && (
							<Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
								<SheetTrigger asChild>
									<Button
										variant="outline"
										size="icon"
										className="size-7 sm:h-10 sm:w-10"
										aria-label="Open filters"
									>
										<Filter className="size-3 sm:h-4 sm:w-4" />
									</Button>
								</SheetTrigger>
								<SheetContent
									side="right"
									className="w-[85vw] max-w-[400px] sm:max-w-[540px] max-h-[100vh] overflow-y-auto"
								>
									<SheetHeader>
										<SheetTitle>Filters</SheetTitle>
									</SheetHeader>
									<div className="flex-grow overflow-y-auto py-4 pr-2">
										{props.slug === 'nodemonkes' && <MonkeDiscoveryControls filters={filters} onChange={updateFilters} />}
										{hasRarityFilter && (
											<div className="mb-4 border-b border-border pb-4">
												<div className="text-sm font-semibold mb-2 px-1">Rarity</div>
												<div className="flex flex-wrap gap-1.5 px-1">
													{RARITY_TIER_OPTIONS.map((label) => {
														const isActive = activeRarityValues.includes(label)
														return (
															<button
																key={label}
																type="button"
																onClick={() => toggleRarityValue(label)}
																className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
																	isActive
																		? 'bg-[#6938EF] text-white border-[#6938EF]'
																		: 'bg-card text-foreground border-border hover:bg-accent'
																}`}
															>
																{label}
															</button>
														)
													})}
												</div>
											</div>
										)}
										{hasAttributes && (
											<TraitFilters
												traits={attributes}
												onChange={updateFilters}
												currentFilters={filters}
											/>
										)}
									</div>
								</SheetContent>
							</Sheet>
						)}
					</div>
				</div>
			</div>

			{/* Scrollable content. The chart tab keeps the side inset (edge to
			    edge clipped the chart's own chrome) and drops the top one, so
			    the terminal sits directly under the tab bar. */}
			<div className={selectedTab === 'chart' ? 'px-4' : 'px-4 md:p-4'}>
				{chartInline && chartExpanded && (
					<div id="collection-inline-chart" className="mb-4 h-[440px] w-full md:h-[520px]">
						<TvCollectionChart slug={props.slug} />
					</div>
				)}
				{selectedTab === 'chart' ? (
					// The full terminal, mounted only once this tab is opened.
					// It pulls a ~2MB library and a history window, so the cost
					// belongs to people who asked for a chart rather than to
					// everyone who opened the collection.
					// Fill the viewport below the site nav, collection header and
					// tab bar. A terminal is something you sit in front of, and
					// the taller it is the more of the series you can actually
					// read; `min-h` keeps it usable on short windows where the
					// subtraction would otherwise leave a sliver.
					<div className="h-[calc(100vh-theme(spacing.72))] min-h-[520px] w-full">
						<TvCollectionChart slug={props.slug} />
					</div>
				) : isLoading && !inscriptions.length ? (
                    <div className="flex justify-center py-8" role="status" aria-label="Loading listings"><Loading /></div>
                ) : (!isLoading && !isRune(props.slug) && inscriptions.length === 0) ||
				(!isLoading && isRune(props.slug) && (escrows?.length === 0 || !escrows)) ? (
					<>
					{!isFungible(props.slug) && <InstantSell slug={props.slug} name={props.meta.name} compact />}
					<EmptyState className="mt-8" title="No Listings">
						{!isRune(props.slug) && (
							<div className="text-center">
								<span
									onClick={handleShowAll}
									className="font-bold cursor-pointer"
									role="button"
									tabIndex={0}
									onKeyDown={onKeyboardClick(handleShowAll)}
								>
									Show All
								</span>{' '}
								to see all inscriptions in this collection
							</div>
						)}
					</EmptyState>
					</>
				) : (
					<InfiniteScroll
						className="overflow-hidden"
						dataLength={inscriptions.length}
						next={fetchMore}
						hasMore={
							isRune(props.slug)
								? (escrows?.length || 0) % LIMIT === 0
								: inscriptions.length % LIMIT === 0
						}
						loader={
							<div className="flex justify-center my-4">
								{!isLoading && inscriptions.length > 0 && <Loading />}
							</div>
						}
						scrollableTarget="collection-feed"
					>
						<div className={styles.cardsGrid}>
							{!isFungible(props.slug) && <MarketTile slug={props.slug} name={props.meta.name} />}
							{inscriptions.map((e, _i) =>
								isRune(props.slug) ? (
									<RuneCard
										key={e.id}
										slug={props.slug}
										inscription={e}
										onClick={() => {
											onSelectBulk(e)
										}}
										selected={bulkSelected[e.id]}
										footer={
											<InscriptionActions
												rune={true}
												inscription={e}
												owner={false}
												actionButtonColor="black"
												onClickBuy={() => {
													onSelectBulk(e)
												}}
											/>
										}
									/>
								) : (
									<InscriptionCardWithPreview
										key={e.id}
										inscription={e}
										onClick={() => {
											onSelectBulk(e)
										}}
										selected={bulkSelected[e.id]}
										disablePreview={disablePreview}
										footer={
											<InscriptionActions
												inscription={e}
												owner={false}
												onlyShowPrice={true}
												collectionCard={{ slug: props.slug, name: props.meta.name }}
												actionButtonColor="black"
												hideView={true}
												onClickBuy={() => {
													onSelectBulk(e)
												}}
												selected={bulkSelected[e.id]}
											/>
										}
									/>
								)
							)}
						</div>
					</InfiniteScroll>
				)}
			</div>
		</section>
	)
}

export default CollectionFeed
